/**
 * Section 01 — Quick Start.
 *
 * The single most-read section: a 5-step setup pathway that takes a brand
 * new user from "I just logged in" to "I'm receiving signals and can
 * one-click execute them" in well under 30 minutes. Each step ends with
 * a "you'll know it worked when" so users can self-verify without help.
 */
import React from 'react';
import { Box, Typography, Stepper, Step, StepLabel, StepContent, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GuideCallout from '../GuideCallout';
import GuideExample from '../GuideExample';
import GuideCodeBlock from '../GuideCodeBlock';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography
    id={`anchor-${id}`}
    variant="h5"
    sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}
  >
    {children}
  </Typography>
);

const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>
    {children}
  </Typography>
);

const Verify: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, my: 1.25, p: 1.25, borderRadius: 1, bgcolor: 'rgba(46,160,67,0.08)', border: '1px solid rgba(46,160,67,0.25)' }}>
    <CheckCircleOutlineIcon sx={{ color: '#2ea043', mt: 0.25 }} fontSize="small" />
    <Box>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#2ea043', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25 }}>
        You'll know it worked when…
      </Typography>
      <Box sx={{ fontSize: '0.88rem', lineHeight: 1.55 }}>{children}</Box>
    </Box>
  </Box>
);

const Section_quick_start: GuideSection = {
  id: 'quick-start',
  label: 'Quick Start',
  group: 'getting-started',
  icon: <RocketLaunchIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Quick Start</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Five steps from blank slate to your first one-click signal execution. Each step takes 2–10 minutes.
        If you only read one section of this guide, read this one.
      </Typography>

      <GuideCallout kind="tip" title="Read the guide alongside the app">
        This page is open in its own browser tab. Keep it visible while you click through the actions in your
        other earngush tab. The <OpenInApp to="/dashboard" label="Open Dashboard" /> chips below jump to the
        right place in the app.
      </GuideCallout>

      <H id="overview">The 5-step path</H>
      <P>
        earngush sits between your trading signals (either TradingView Pine scripts or the in-house RockBand
        scanner) and your broker (Delta Exchange for crypto, Zerodha for Indian equities / F&O). The five
        steps below set up that bridge end-to-end. Skip any step that's already done — they're independent.
      </P>

      <Stepper orientation="vertical" sx={{ mb: 3 }}>
        <Step active>
          <StepLabel><strong>Connect a broker</strong> · 5 min</StepLabel>
          <StepContent>Saves your API key/secret and verifies broker reachability.</StepContent>
        </Step>
        <Step active>
          <StepLabel><strong>Set risk basics</strong> · 2 min</StepLabel>
          <StepContent>Daily-loss cap and the global order-placement kill switch.</StepContent>
        </Step>
        <Step active>
          <StepLabel><strong>Build a watchlist</strong> · 5 min</StepLabel>
          <StepContent>Tells scans + strategies which symbols you care about.</StepContent>
        </Step>
        <Step active>
          <StepLabel><strong>Wire up signals</strong> · 10 min</StepLabel>
          <StepContent>Either a TradingView webhook (per-user secret) or enable an in-house strategy.</StepContent>
        </Step>
        <Step active>
          <StepLabel><strong>Receive + execute your first signal</strong> · ongoing</StepLabel>
          <StepContent>Verify in Dashboard → Recent Alerts; one-click execute.</StepContent>
        </Step>
      </Stepper>

      {/* ───────────────────── STEP 1 ───────────────────── */}
      <H id="step-1-broker">Step 1 — Connect a broker</H>
      <P>
        Go to <OpenInApp to="/settings" label="Settings → Broker Credentials" /> and add the broker you'll trade
        through. earngush supports both at once if you trade both — Delta for crypto perpetuals, Zerodha for
        NSE/BSE/NFO. You'll need API credentials issued by the broker.
      </P>
      <P>
        <strong>Delta Exchange:</strong> generate an API key + secret in the Delta web console. Paste both into
        the Delta Exchange row in Broker Credentials. Important: Delta requires <em>IP whitelisting</em>; the
        platform's outbound IP (167.86.119.214) must be in your Delta API allow-list. Without it you'll see a
        <code> ip_not_whitelisted_for_api_key </code> error on every call.
      </P>
      <P>
        <strong>Zerodha:</strong> save your Kite Connect <em>API key + API secret</em>, then click "Login with
        Zerodha" — you'll be redirected to Zerodha's OAuth screen, log in with your trading password + TOTP,
        and you'll bounce back to earngush with a fresh access token. Zerodha access tokens expire daily at
        ~6 AM IST; you'll need to re-login each morning or use the scheduled-login automation.
      </P>
      <P>
        Click <strong>Test Connection</strong> on the saved row. A green check means broker reachability is good.
        A red error usually means (a) IP not whitelisted, (b) Zerodha access token expired, or (c) wrong key/secret.
      </P>
      <Verify>
        The broker row in Settings shows a green dot and a most-recent <code>lastConnectedAt</code> timestamp
        within the last minute. The Dashboard's RiskCockpit shows your real Available Margin (not zero).
      </Verify>

      <GuideCallout kind="danger" title="Your API secret is encrypted at rest">
        Both apiKey and apiSecret are AES-256-GCM encrypted in the database (since R80). Even an admin reading
        your row in the DB sees a ciphertext. The secret never leaves the server in plaintext.
      </GuideCallout>

      {/* ───────────────────── STEP 2 ───────────────────── */}
      <H id="step-2-risk">Step 2 — Set risk basics</H>
      <P>
        Open <OpenInApp to="/settings" label="Settings → Trading Preferences" />. Three knobs matter on day one:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText
            primary={<><strong>Daily-Loss Circuit Breaker</strong> — set to a rupee amount you can absorb if
              today goes wrong (e.g. ₹5,000). Toggle <em>enforced</em> ON so the gate blocks new orders once the
              cap is reached.</>}
          />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText
            primary={<><strong>Portfolio Reserve %</strong> — fraction of portfolio that must stay as free
              margin (e.g. 20% means: even when fully deployed, keep ≥20% cash). The dashboard goes red when
              you breach this.</>}
          />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}>
          <ListItemText
            primary={<><strong>Auto Trade</strong> — leave this OFF for your first week. With it OFF, every
              alert lands in Dashboard → Recent Alerts and waits for you to one-click execute. With it ON,
              alerts marked CONFIRMED=Y fire immediately at the broker.</>}
          />
        </ListItem>
      </List>
      <P>
        Above all of these sits the <strong>Order Placement kill switch</strong> — the master "stop opening
        new positions" switch on the Dashboard. Flip it OFF whenever you want a hard pause on new entries
        without touching individual settings. Protective exits (stop-loss, target, trailing, manual closes)
        still go through, so open positions remain manageable.
      </P>
      <Verify>
        Dashboard → top row shows your daily-loss cap as a coloured bar (green = safe, amber/red = nearing
        breach). Trading Controls section shows "Order Placement: LIVE" with the toggle in the right state.
      </Verify>

      {/* ───────────────────── STEP 3 ───────────────────── */}
      <H id="step-3-watchlist">Step 3 — Build a watchlist</H>
      <P>
        Go to <OpenInApp to="/strategies" label="Strategies → Watchlists" />. A watchlist is just a named bag of
        symbols. You can have many — e.g. one called "Indices" containing NIFTY/BANKNIFTY, another
        "BTC pairs" containing BTCUSD/ETHUSD/BNBUSD. Watchlists feed scans, the Dashboard heatmap, and the
        Price Watch panel.
      </P>
      <P>
        Add symbols by typing the ticker. The autocomplete is fuzzy — "btc" finds BTCUSD, "nifty" finds
        NIFTY (the equity index), NIFTYFUT (futures), etc.
      </P>
      <Verify>
        Dashboard → Watchlist heatmap (just above Trades Management) shows tiles for every symbol with a
        24-hour % change badge. Click any tile and a chart opens.
      </Verify>

      {/* ───────────────────── STEP 4 ───────────────────── */}
      <H id="step-4-signals">Step 4 — Wire up signals</H>
      <P>
        Two independent paths. You can use either, or both at the same time.
      </P>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>Option A — TradingView webhook</Typography>
      <P>
        Use this when you've built your own Pine script on TradingView and want its alerts to execute through
        earngush. Open <OpenInApp to="/app-guide#tradingview-setup" label="App Guide → TradingView Setup" /> for
        the full walkthrough. The short version:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary="Settings → Broker Credentials: copy your own webhook URL (it carries your secret)." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary="In TradingView alert dialog: paste the URL, add the secret as a custom header." />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary="Set the alert message to a JSR_CLR pipe-delimited line (see template below)." />
        </ListItem>
      </List>
      <GuideCodeBlock label="Minimal alert message">
{`JSR_CLR|TICKER={{ticker}}|TF={{interval}}|SIGNAL=BUY|ENTRY_LEVEL={{close}}|ISL={{low}}|CONFIRMED=Y`}
      </GuideCodeBlock>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2 }}>Option B — In-house strategy</Typography>
      <P>
        Use this when you don't have a Pine script or want to use earngush's built-in scanner. Go to
        <OpenInApp to="/strategies" label="Strategies" /> and select <strong>RockBand</strong> — the active
        in-house engine for scheduled scans and auto-trade. (Sniper and DailyBlast are legacy and no longer
        run scheduled scans.) RockBand has a <strong>Symbols</strong> tab (pick the watchlist), a
        <strong> Scan</strong> tab (run on-demand or schedule periodic) and a <strong>Signal Log</strong> tab.
      </P>
      <P>
        First time: open RockBand, attach a watchlist, click <em>Scan Now</em>. Within ~30 seconds you'll see
        signal rows appear in the log. Each signal is also pushed as an alert to your dashboard.
      </P>
      <Verify>
        Dashboard → Recent Alerts shows new rows. Each row has a side badge (BUY/SHORT), a price, a
        confidence/strength chip, and (if Auto Trade is OFF) an "Execute" button.
      </Verify>

      {/* ───────────────────── STEP 5 ───────────────────── */}
      <H id="step-5-execute">Step 5 — Receive + execute your first signal</H>
      <P>
        With Auto Trade OFF (the recommended starting position), every alert is a one-click decision. On the
        Dashboard:
      </P>
      <List dense sx={{ pl: 1 }}>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Click the alert row to expand. Verify the symbol, side, entry, SL, and target.</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Adjust quantity if needed (defaults to your per-instrument <em>Investment Amount</em> ÷ price).</>} />
        </ListItem>
        <ListItem sx={{ display: 'list-item', listStyleType: 'decimal', ml: 3 }}>
          <ListItemText primary={<>Click <strong>Execute</strong> — the order goes to the broker; the alert moves to "Acknowledged".</>} />
        </ListItem>
      </List>
      <P>
        Once filled, the position appears in <OpenInApp to="/trades" label="Trades" /> with full event timeline
        (ENTRY_FILLED → TSL_ACTIVATED → … → CLOSE_FIRED). You can drag the SL/Target lines on the chart and
        the platform updates the broker order in real time.
      </P>
      <Verify>
        <strong>Trades</strong> page shows your position with status OPEN. Click the row to expand — you see a
        vertical event timeline. The Dashboard's "Open Positions" tile increments by 1.
      </Verify>

      <GuideExample title="A typical day-1 flow">
        09:15 — log in, broker shows Available Margin ₹50,000. Daily-loss cap set to ₹2,500 (5%).<br />
        09:18 — Enable RockBand on the "Indices" watchlist, schedule 5-min scans.<br />
        09:35 — Dashboard chimes (speech-alert + Telegram). RockBand saw a long signal on BANKNIFTY at 49,820.<br />
        09:36 — You expand the alert, qty defaults to 15 (1 lot), SL at 49,750. Click Execute.<br />
        09:37 — Trade visible in Trades; chart shows the SL line. Trailing-SL activates once price moves +0.4%.<br />
        10:12 — TSL trails to 49,890; price hits 49,950, you tap "Close" → realised P&L +₹1,950.
      </GuideExample>

      <H id="next-steps">Next steps</H>
      <P>
        Once you've placed and closed a first trade, the rest of the guide makes a lot more sense:
      </P>
      <List dense>
        <ListItem sx={{ pl: 0 }}>
          <ListItemIcon sx={{ minWidth: 28 }}><RocketLaunchIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={<><strong>Core Concepts</strong> — the signal-flow diagram + position lifecycle. Read this next.</>} />
        </ListItem>
        <ListItem sx={{ pl: 0 }}>
          <ListItemIcon sx={{ minWidth: 28 }}><RocketLaunchIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={<><strong>Risk Management</strong> — daily-loss cap mechanics, R-multiple thinking, kill-switch usage.</>} />
        </ListItem>
        <ListItem sx={{ pl: 0 }}>
          <ListItemIcon sx={{ minWidth: 28 }}><RocketLaunchIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={<><strong>Charts</strong> — the most powerful page; indicator overlays, order capture, replay mode.</>} />
        </ListItem>
        <ListItem sx={{ pl: 0 }}>
          <ListItemIcon sx={{ minWidth: 28 }}><RocketLaunchIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={<><strong>Strategies</strong> — RockBand (the active in-house engine) plus the legacy Sniper / DailyBlast deep dives, each with a worked example. See also the <strong>AutoTrade Lifecycle</strong> section.</>} />
        </ListItem>
      </List>
    </>
  ),
  searchIndex: [
    { heading: 'The 5-step path',     anchor: 'overview',          body: 'overview five step path' },
    { heading: 'Connect a broker',    anchor: 'step-1-broker',     body: 'broker delta zerodha api key secret ip whitelist' },
    { heading: 'Set risk basics',     anchor: 'step-2-risk',       body: 'daily loss cap reserve auto trade kill switch' },
    { heading: 'Build a watchlist',   anchor: 'step-3-watchlist',  body: 'watchlist symbols heatmap' },
    { heading: 'Wire up signals',     anchor: 'step-4-signals',    body: 'tradingview webhook in-house strategy sniper rockband dailyblast' },
    { heading: 'Execute first signal',anchor: 'step-5-execute',    body: 'execute alert dashboard trade' },
    { heading: 'Next steps',          anchor: 'next-steps',        body: 'next steps further reading' },
  ],
};

export default Section_quick_start;
